import React, { useState } from "react";
import {
  RadioFieldComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";

interface RadioFieldProps {
  config: RadioFieldComponent;
  value?: string | number | boolean;
  onChange?: (name: string, value: string | number | boolean) => void;
}

export const RadioField: React.FC<RadioFieldProps> = ({
  config,
  value,
  onChange,
}) => {
  const [error, setError] = useState<string>("");

  const validateField = (
    inputValue: string | number | boolean | undefined
  ): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (inputValue === undefined || inputValue === "")
            return rule.message || "Please select an option";
          break;
      }
    }
    return "";
  };

  const handleChange = (selectedValue: string | number | boolean) => {
    const validationError = validateField(selectedValue);
    setError(validationError);
    onChange?.(config.name, selectedValue);
  };

  // Convert value to string for React input elements (booleans become "true"/"false")
  const getInputValue = (val: string | number | boolean): string => {
    if (typeof val === "boolean") return String(val);
    return String(val);
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const baseStyles: React.CSSProperties = {
    margin: config.style?.margin || 0,
    padding: config.style?.padding || 0,
  };

  return (
    <div className={getWidthClass()} style={baseStyles} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div
        className={`flex flex-wrap ${config.direction === "row" ? "flex-row gap-4" : "flex-row gap-2"
          }`}
      >
        {config.options.map((option, index) => (
          <div
            key={index}
            className={`flex items-center p-2 rounded-md transition-colors duration-200 ${option.disabled
                ? "cursor-not-allowed opacity-70"
                : "cursor-pointer hover:bg-primary-100 dark:hover:bg-primary-900/30"
              }`}
            onClick={() =>
              !option.disabled && !config.disabled && handleChange(option.value)
            }
          >
            <input
              type="radio"
              name={config.name}
              value={getInputValue(option.value)}
              checked={value === option.value}
              onChange={() =>
                !option.disabled &&
                !config.disabled &&
                handleChange(option.value)
              }
              disabled={option.disabled || config.disabled}
              className="w-[18px] h-[18px] mr-2 cursor-pointer"
              style={{ accentColor: "var(--color-primary-500, #35ba5b)" }}
            />
            <span className="text-sm font-normal text-gray-900 dark:text-gray-100 cursor-pointer select-none font-sans">
              {option.label}
            </span>
          </div>
        ))}
      </div>

      {error && (
        <div className="text-danger-500 dark:text-danger-400 text-sm mt-2 flex items-center">
          <span className="mr-1">⚠️</span>
          {error}
        </div>
      )}

      {config.helperText && !error && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-2">{config.helperText}</div>
      )}
    </div>
  );
};
